import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authService, UserInfo } from '../services/auth';
import { sessionService } from '../services/session';
import { apiService } from '../services/api';

interface AuthContextType {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isManager: boolean;
  canCreateLeague: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  loading: boolean;
  joinError: string | null;
  clearJoinError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Synchronous bootstrap — runs once before first render to avoid loading flash.
// Deliberately does NOT trust JWT-derived `groups` for any privileged UI;
// `isAdmin`/`isManager` are populated only after a successful /users/me fetch.
//
// On cold start, in-memory access/id tokens are gone but the refresh token
// survives in localStorage. We optimistically hydrate from cached user_info
// and let the background effect (or the first 401-retry in api.ts) call
// refreshTokens() to mint new access/id tokens.
function getInitialUser(): UserInfo | null {
  try {
    const tokens = authService.getTokens();
    if (tokens?.idToken) {
      try {
        const payload = JSON.parse(atob(tokens.idToken.split('.')[1]));
        const userInfo: UserInfo = {
          userId: payload.sub,
          email: payload.email,
        };
        const cached = authService.getUserInfoFromStorage();
        if (cached?.displayName) {
          userInfo.displayName = cached.displayName;
        }
        authService.storeUserInfo(userInfo);
        return userInfo;
      } catch {
        authService.clearTokens();
        authService.clearUserInfo();
      }
    } else if (tokens?.refreshToken) {
      // Cold start: no in-memory access token, but a refresh token survived.
      // Surface the cached user so the UI renders authenticated; the background
      // effect will refresh tokens + reconcile profile.
      const cached = authService.getUserInfoFromStorage();
      if (cached?.userId) return cached;
    }
  } catch {
    authService.clearTokens();
    authService.clearUserInfo();
  }
  return null;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(getInitialUser);
  const [loading] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Fetch fresh profile + groups in background (non-blocking).
    // Groups are the only trustworthy source for admin gating — they come
    // from the API Gateway Cognito authorizer claims server-side.
    if (!user) return;

    (async () => {
      // Cold start: only the refresh token survived the browser restart.
      // Mint fresh access/id tokens before any authed API call.
      if (!authService.getAccessToken()) {
        const refreshed = await authService.refreshTokens();
        if (!refreshed) {
          // Refresh failed (revoked, expired, network) — drop to logged-out.
          authService.clearTokens();
          authService.clearUserInfo();
          setUser(null);
          return;
        }
      }

      try {
        const profile = await apiService.getUserProfile();
        setUser(prev => prev ? {
          ...prev,
          displayName: profile.displayName ?? prev.displayName,
          groups: profile.groups ?? [],
        } : prev);
      } catch (error) {
        console.warn('Could not fetch user profile:', error);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Returns true when login handled navigation internally (join intent case),
  // so the caller knows not to call its own onSuccess/navigate.
  const login = async (email: string, password: string): Promise<boolean> => {
    await authService.login(email, password);
    // authService.login() stored userInfo, but `groups` is intentionally not
    // persisted. Re-fetch /users/me to get verified groups for admin gating.
    const userInfo = authService.getUserInfoFromStorage()!;
    try {
      const profile = await apiService.getUserProfile();
      userInfo.groups = profile.groups ?? [];
      if (profile.displayName) userInfo.displayName = profile.displayName;
    } catch (error) {
      console.warn('Could not fetch profile groups after login:', error);
      userInfo.groups = [];
    }
    setUser(userInfo);

    // Check for pending registration intent (invite token from registration flow)
    const registrationToken = sessionService.getRegistrationIntent();
    if (registrationToken) {
      try {
        const result = await apiService.joinLeagueByToken(registrationToken);
        sessionService.clearRegistrationIntent();

        // Navigate to the league standings page for the league they just joined
        navigate(`/standings/${result.league.leagueId}`, {
          state: {
            joinSuccess: true,
            leagueName: result.league.name,
          },
        });
        return true;
      } catch (error) {
        console.error('Failed to join league after registration:', error);
        sessionService.clearRegistrationIntent();
        // Continue to check for regular join intent
      }
    }

    // Check for pending join intent after successful login
    const joinIntent = sessionService.getJoinIntent();
    if (joinIntent) {
      try {
        const result = await apiService.joinLeagueByToken(joinIntent);
        sessionService.clearJoinIntent();

        // Navigate to the league standings page for the league they just joined
        navigate(`/standings/${result.league.leagueId}`, {
          state: {
            joinSuccess: true,
            leagueName: result.league.name,
          },
        });
      } catch (error) {
        console.error('Failed to join league after login:', error);

        // Map error to user-friendly message
        const errorMessage = error instanceof Error ? error.message : 'Failed to join league';
        let userMessage = errorMessage;

        if (errorMessage.includes('422') || errorMessage.includes('capacity')) {
          userMessage = 'This league is at maximum capacity and cannot accept new members';
        } else if (errorMessage.includes('410') || errorMessage.includes('deleted')) {
          userMessage = 'This league has been deleted and is no longer available';
        } else if (errorMessage.includes('409') || errorMessage.includes('already')) {
          userMessage = 'You are already a member of this league';
          sessionService.clearJoinIntent();
          navigate('/leagues');
          return true;
        } else if (errorMessage.includes('400') || errorMessage.includes('invalid')) {
          userMessage = 'Invalid or expired invitation link';
        } else {
          userMessage = 'Failed to join league. Please try again.';
        }

        // Store error for display and clear the intent
        setJoinError(userMessage);
        sessionService.clearJoinIntent();

        // Navigate to leagues page where error can be displayed
        navigate('/leagues', { state: { joinError: userMessage } });
      }
      return true; // navigation was handled
    }

    return false; // caller should navigate
  };

  const clearJoinError = () => {
    setJoinError(null);
  };

  const logout = async () => {
    // Clear user state immediately to prevent any race conditions
    setUser(null);

    // Clears tokens, user info, and per-user browser storage (localStorage
    // read receipts, service-worker api-cache).
    await authService.logout();

    // Wipe the in-memory React Query cache so the next user on this tab
    // does not see the previous user's leagues, predictions, etc. Query
    // keys are not user-scoped, so without this the cache leaks across logins.
    queryClient.clear();
  };

  const refreshUser = async () => {
    try {
      const tokens = authService.getTokens();
      if (tokens?.accessToken) {
        const userInfo = await authService.getUserInfo(tokens.accessToken);

        try {
          const profile = await apiService.getUserProfile();
          if (profile.displayName) {
            userInfo.displayName = profile.displayName;
          }
          // Backend-derived groups are the only trustworthy source.
          userInfo.groups = profile.groups ?? [];
        } catch (error) {
          console.warn('Could not fetch user profile:', error);
        }

        authService.storeUserInfo(userInfo);
        setUser(userInfo);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const isAdmin = user?.groups?.includes('SuperAdmins') || false;
  const isManager = user?.groups?.includes('Managers') || false;
  const canCreateLeague = isAdmin || isManager;

  const value = {
    user,
    isAuthenticated: !!user,
    isAdmin,
    isManager,
    canCreateLeague,
    login,
    logout,
    refreshUser,
    loading,
    joinError,
    clearJoinError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
